import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Grid,
  Autocomplete, Tooltip,
} from '@mui/material';
import { Edit, Delete, CheckCircle, Schedule } from '@mui/icons-material';
import api from '../../api/client';
import { ensureTimezone } from '../../utils/time';

interface ScheduledCallRecord {
  id: number;
  scheduled_datetime: string;
  advance_minutes: number;
  call_type_id: number;
  call_type_code: string;
  call_type_name: string;
  priority: number;
  location_address: string;
  location_lat: number | null;
  location_lng: number | null;
  location_apt: string;
  cross_street: string;
  reporting_party: string;
  reporting_phone: string;
  narrative: string;
  agency_id: number | null;
  agency_name: string;
  is_created: boolean;
  created_call_id: number | null;
  created_at: string;
}

interface CallType { id: number; code: string; name: string; priority: number; }
interface Agency { id: number; name: string; }
interface CallPriorityOption { id: number; code: number; name: string; color: string; sort_order: number; }

const PRIORITY_COLORS: Record<number, string> = { 1: '#f44336', 2: '#ff9800', 3: '#2196f3', 4: '#4caf50', 5: '#9e9e9e' };

const DENSE_CELL = { py: 0.5, px: 1, fontSize: 13, lineHeight: 1.3 } as const;
const DENSE_HEADER = { ...DENSE_CELL, fontWeight: 700 } as const;

export default function ScheduledCallsPage() {
  const [items, setItems] = useState<ScheduledCallRecord[]>([]);
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [callPriorities, setCallPriorities] = useState<CallPriorityOption[]>([]);
  const [tzReady, setTzReady] = useState(false);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState<ScheduledCallRecord | null>(null);
  const [editForm, setEditForm] = useState({
    scheduled_date: '',
    scheduled_time: '',
    advance_minutes: 30,
    call_type_id: 0,
    priority: 3,
    location_address: '',
    location_lat: null as number | null,
    location_lng: null as number | null,
    location_apt: '',
    cross_street: '',
    reporting_party: '',
    reporting_phone: '',
    narrative: '',
    agency_id: null as number | null,
  });

  // Delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const loadAll = useCallback(() => {
    api.get('/scheduled-calls').then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    ensureTimezone().then(() => setTzReady(true));
    loadAll();
    api.get('/call-types').then((r) => setCallTypes(r.data)).catch(() => {});
    api.get('/agencies').then((r) => setAgencies(r.data)).catch(() => {});
    api.get('/call-priorities').then((r) => setCallPriorities(r.data)).catch(() => {});
  }, [loadAll]);

  const handleEdit = (item: ScheduledCallRecord) => {
    const dt = new Date(item.scheduled_datetime);
    const pad = (n: number) => String(n).padStart(2, '0');
    setEditForm({
      scheduled_date: `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`,
      scheduled_time: `${pad(dt.getHours())}:${pad(dt.getMinutes())}`,
      advance_minutes: item.advance_minutes,
      call_type_id: item.call_type_id,
      priority: item.priority,
      location_address: item.location_address,
      location_lat: item.location_lat,
      location_lng: item.location_lng,
      location_apt: item.location_apt,
      cross_street: item.cross_street,
      reporting_party: item.reporting_party,
      reporting_phone: item.reporting_phone,
      narrative: item.narrative,
      agency_id: item.agency_id,
    });
    setEditItem(item);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editItem) return;
    try {
      const scheduledDatetime = `${editForm.scheduled_date}T${editForm.scheduled_time}:00`;
      await api.put(`/scheduled-calls/${editItem.id}`, {
        scheduled_datetime: scheduledDatetime,
        advance_minutes: editForm.advance_minutes,
        call_type_id: editForm.call_type_id,
        priority: editForm.priority,
        location_address: editForm.location_address,
        location_lat: editForm.location_lat,
        location_lng: editForm.location_lng,
        location_apt: editForm.location_apt,
        cross_street: editForm.cross_street,
        reporting_party: editForm.reporting_party,
        reporting_phone: editForm.reporting_phone,
        narrative: editForm.narrative,
        agency_id: editForm.agency_id,
      });
      setEditOpen(false);
      setEditItem(null);
      loadAll();
    } catch { /* ignore */ }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/scheduled-calls/${deleteId}`);
      setDeleteOpen(false);
      setDeleteId(null);
      loadAll();
    } catch { /* ignore */ }
  };

  const formatScheduledTime = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  if (!tzReady) return null;

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            <Schedule sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
            Scheduled Calls
          </Typography>
          <Chip label={`${items.filter((i) => !i.is_created).length} pending`} color="primary" />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={DENSE_HEADER}>Scheduled For</TableCell>
                <TableCell sx={DENSE_HEADER}>Advance (min)</TableCell>
                <TableCell sx={DENSE_HEADER}>Priority</TableCell>
                <TableCell sx={DENSE_HEADER}>Type</TableCell>
                <TableCell sx={DENSE_HEADER}>Location</TableCell>
                <TableCell sx={DENSE_HEADER}>Reporting Party</TableCell>
                <TableCell sx={DENSE_HEADER}>Agency</TableCell>
                <TableCell sx={DENSE_HEADER}>Status</TableCell>
                <TableCell sx={DENSE_HEADER} width={100}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover sx={{ opacity: item.is_created ? 0.6 : 1 }}>
                  <TableCell sx={{ ...DENSE_CELL, fontWeight: 700, fontFamily: 'monospace' }}>
                    {formatScheduledTime(item.scheduled_datetime)}
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>{item.advance_minutes} min</TableCell>
                  <TableCell sx={DENSE_CELL}>
                    <Chip size="small" label={`P${item.priority}`} sx={{ bgcolor: PRIORITY_COLORS[item.priority], color: '#fff', fontWeight: 700, height: 20, fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={DENSE_CELL}>{item.call_type_code} - {item.call_type_name}</TableCell>
                  <TableCell sx={DENSE_CELL}>{item.location_address}</TableCell>
                  <TableCell sx={DENSE_CELL}>{item.reporting_party}</TableCell>
                  <TableCell sx={DENSE_CELL}>{item.agency_name}</TableCell>
                  <TableCell sx={DENSE_CELL}>
                    {item.is_created ? (
                      <Chip size="small" icon={<CheckCircle />} label="Created" color="success" sx={{ height: 22 }} />
                    ) : (
                      <Chip size="small" icon={<Schedule />} label="Pending" color="warning" sx={{ height: 22 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...DENSE_CELL, p: 0 }}>
                    {!item.is_created && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => { setDeleteId(item.id); setDeleteOpen(true); }}>
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No scheduled calls. Use the "Schedule Call" button when creating a new call.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Scheduled Call Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Scheduled Call</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Scheduled Date"
                type="date"
                value={editForm.scheduled_date}
                onChange={(e) => setEditForm({ ...editForm, scheduled_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Scheduled Time"
                type="time"
                value={editForm.scheduled_time}
                onChange={(e) => setEditForm({ ...editForm, scheduled_time: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Advance (minutes)"
                type="number"
                value={editForm.advance_minutes}
                onChange={(e) => setEditForm({ ...editForm, advance_minutes: Number(e.target.value) })}
                slotProps={{ input: { inputProps: { min: 0 } } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Location / Address"
                value={editForm.location_address}
                onChange={(e) => setEditForm({ ...editForm, location_address: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField fullWidth label="Apt / Suite" value={editForm.location_apt} onChange={(e) => setEditForm({ ...editForm, location_apt: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Cross Street" value={editForm.cross_street} onChange={(e) => setEditForm({ ...editForm, cross_street: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Latitude" type="number" value={editForm.location_lat ?? ''} onChange={(e) => setEditForm({ ...editForm, location_lat: e.target.value ? Number(e.target.value) : null })} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField fullWidth label="Longitude" type="number" value={editForm.location_lng ?? ''} onChange={(e) => setEditForm({ ...editForm, location_lng: e.target.value ? Number(e.target.value) : null })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={callTypes}
                getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                value={callTypes.find((ct) => ct.id === editForm.call_type_id) || null}
                onChange={(_, val) => setEditForm({ ...editForm, call_type_id: val?.id || 0, priority: val?.priority ?? editForm.priority })}
                renderInput={(params) => <TextField {...params} label="Call Type" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={callPriorities}
                getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                value={callPriorities.find((cp) => cp.code === editForm.priority) || null}
                onChange={(_, val) => setEditForm({ ...editForm, priority: val?.code ?? 3 })}
                renderInput={(params) => <TextField {...params} label="Priority" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={agencies}
                getOptionLabel={(opt) => opt.name}
                value={agencies.find((a) => a.id === editForm.agency_id) || null}
                onChange={(_, val) => setEditForm({ ...editForm, agency_id: val?.id || null })}
                renderInput={(params) => <TextField {...params} label="Agency" />}
                openOnFocus
                autoHighlight
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Reporting Party" value={editForm.reporting_party} onChange={(e) => setEditForm({ ...editForm, reporting_party: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Phone" value={editForm.reporting_phone} onChange={(e) => setEditForm({ ...editForm, reporting_phone: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={3} label="Narrative" value={editForm.narrative} onChange={(e) => setEditForm({ ...editForm, narrative: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!editForm.scheduled_date || !editForm.scheduled_time || !editForm.call_type_id}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Scheduled Call</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this scheduled call?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
